import { Injectable } from '@angular/core';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as AppActions from '@store/actions/app.actions';
import * as ReservationListActions from '@store/actions/reservation-list.actions';
import * as ReservationActions from '@store/actions/reservation.actions';
import { State } from '@store/index';
import * as ReservationListSelectors from '@store/selectors/reservation-list.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import * as ReservationSelectors from '@store/selectors/reservation.selectors';
import { EMPTY, interval, of } from 'rxjs';
import { catchError, concatMap, delay, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { TablesPlanApiService } from 'src/app/apis/tables-plan-api.service';
import { CapacitorStorageService } from 'src/app/lib/capacitor-storage.service';
import { LocalStorageService } from 'src/app/lib/local-storage.service';
import { TablesPlanActions } from '../actions/tables-plan.actions';
import { ReservationApiService } from 'src/app/apis/reservation-api.service';
import * as AppSelectors from '@store/selectors/app.selectors';
import { Reservation } from '@model/reservation';
import { ReservationService } from 'src/app/lib/reservation.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { BatchSynchRequestUpdated } from '@model/tables-plan';
import { ToastService } from 'src/app/lib/toast.service';
import { Toast } from 'bootstrap';
import { ErrorResponse } from '@model/error';
import { TranslateService } from '@ngx-translate/core';
import { TablesPlanService } from 'src/app/tables-plan/tables-plan.service';

const exactMatchOptions: IsActiveMatchOptions = {
  fragment: 'exact',
  matrixParams: 'exact',
  paths: 'exact',
  queryParams: 'subset'
};

@Injectable()
export class TablesPlanEffects {

  loadTablesPlan$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.loadTablesPlan, ReservationListActions.setDate, TablesPlanActions.restoreRegularTablesPlanSuccess),
      filter(() => this.router.isActive('/tables-plan', exactMatchOptions)),
      withLatestFrom(this.store.select(ReservationListSelectors.selectDate)),
      concatMap(([action, date]) => {

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        return this.tablesPlanApiService.getTablesPlan(restaurantId, date)
          .pipe(
            map(data => {
              return TablesPlanActions.loadTablesPlanSuccess(data);
            }),
            catchError(error => {
              return of(TablesPlanActions.loadTablesPlanFailure({ error }));
            }))
      })
    );
  });

  setDate$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.setDate, TablesPlanActions.loadTablesPlanSuccess),
      withLatestFrom(
        this.store.select(TablesPlanSelectors.selectWorkingDate),
      ),
      concatMap(([action, workingDate]) => {

        if (workingDate == null) {
          return EMPTY;
        }

        return of(workingDate).pipe(
          map(workingDate => {
            return TablesPlanActions.setWorkingDate({ workingDate });
          }))
      })
    );
  });

  setWorkingDate$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.setWorkingDate),
      withLatestFrom(
        this.store.select(TablesPlanSelectors.selectWorkingDate),
        this.store.select(TablesPlanSelectors.selectSections),
        this.store.select(TablesPlanSelectors.selectSelectedSection),
      ),
      concatMap(([action, workingDate, sections, selectedSection]) => {

        if (workingDate == null) {
          return EMPTY;
        }

        const workingDateSections = (sections ?? []).filter(s => s.workingDateId === workingDate?.workingDateId).sort((a, b) => {
          if (a.sectionOrder < b.sectionOrder) {
            return -1;
          }
          if (a.sectionOrder > b.sectionOrder) {
            return 1;
          }

          return 0;
        });

        let section = selectedSection != null
          ? workingDateSections.find(s => s.sectionName === selectedSection?.sectionName)
          : null;

        if (section == null) {
          section = workingDateSections[0];
        }

        return of(section).pipe(
          map(section => {
            return TablesPlanActions.setSection({ section });
          }))
      })
    );
  });

  loadTablesPlanOptions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TablesPlanActions.loadTablesPlanOptions),
      concatMap(async () => {
        const zoom = await this.capacitorStorageService.getTablesPlanZoom() ?? 0.8;
        const options = await this.capacitorStorageService.getTablesPlanOptions();
        return TablesPlanActions.loadTablesPlanOptionsSuccess({ zoom, options });
      })
    );
  });

  loadHasOpenedTablesPlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TablesPlanActions.loadHasOpenedTablesPlan),
      concatMap(async () => {
        const hasOpenedTablesPlan = await this.capacitorStorageService.getHasOpenedTablesPlan();
        return TablesPlanActions.setHasOpenedTablesPlan({ hasOpenedTablesPlan });
      })
    );
  });

  setHasOpenedTablesPlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TablesPlanActions.setHasOpenedTablesPlan),
      withLatestFrom(
        this.store.select(TablesPlanSelectors.selectHasOpenedTablesPlan),
      ),
      concatMap(async ([action, hasOpenedTablesPlan]) => {
        this.capacitorStorageService.setHasOpenedTablesPlan(hasOpenedTablesPlan);
        return TablesPlanActions.emptyAction();
      })
    );
  });

  setZoom$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.setZoom),
      withLatestFrom(
        this.store.select(TablesPlanSelectors.selectZoom),
      ),
      concatMap(([action, zoom]) => {
        this.capacitorStorageService.setTablesPlanZoom(zoom);
        return EMPTY;
      })
    );
  }, { dispatch: false });

  setOptions$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.setShowSurname, TablesPlanActions.setShowProgress, TablesPlanActions.setShowTablesConnection),
      withLatestFrom(
        this.store.select(TablesPlanSelectors.selectShowSurname),
        this.store.select(TablesPlanSelectors.selectShowProgress),
        this.store.select(TablesPlanSelectors.selectShowTablesConnection),
      ),
      concatMap(([action, showSurname, showProgress, showTablesConnection]) => {
        this.capacitorStorageService.setTablesPlanOptions({ showSurname, showProgress, showTablesConnection });
        return EMPTY;
      })
    );
  }, { dispatch: false });

  setReservationAllocatedTables$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationActions.setReservationAllocatedTables),
      filter(() => this.router.isActive('/tables-plan', exactMatchOptions)),
      concatMap(async () => {
        return ReservationActions.submitReservation();
      })
    );
  });

  // optimization (14/06/2024)
  // refreshReservationsListOnInterval$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(AppActions.setMenus),
  //     switchMap((action, num) => {
  //       return interval(this.localStorageService.getRefreshInterval()).pipe(
  //         map(data => {

  //           if (!this.router.isActive('/tables-plan', exactMatchOptions)) {
  //             return TablesPlanActions.noAction();
  //           }

  //           return TablesPlanActions.loadTablesPlan();
  //         })
  //       )
  //     })
  //   );
  // });

  setReservationAllocatedTablesAndSubmit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TablesPlanActions.setReservationAllocatedTablesAndSubmit),
      withLatestFrom(this.store.select(AppSelectors.selectIsOffline)),
      concatMap(([action, isOffline]) => {

        if (action.reservation == null) {
          return EMPTY;
        }

        const reservation: Reservation = JSON.parse(JSON.stringify(action.reservation)); // make object mutable

        reservation.reservationDetails.allocatedTables = action.allocatedTables.join(',');

        if (isOffline === true) {
          return of({}).pipe(
            map(data => {
              return ReservationListActions.addPendingReservation({ reservation: reservation });
            }))
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        this.store.dispatch({ type: ReservationActions.setIsSubmiting.type, isSubmiting: true });

        this.reservationService.prepareForSave(reservation);

        this.showLoader();

        return this.reservationApiService.createOrUpdateReservation(parseInt(restaurantId), reservation)
          .pipe(
            map(data => {
              this.hideLoader();
              this.store.dispatch({ type: ReservationActions.setIsSubmiting.type, isSubmiting: false });
              if (data?.ErrorCode != null && data?.ErrorCode.length > 0) {
                return ReservationActions.submitReservationFailure(data?.ErrorCode);
              }
              return TablesPlanActions.emptyAction(); //ReservationActions.submitReservationSuccess({ reservation: this.reservationService.initReservation(reservation) });
            }),
            catchError(error => {
              this.hideLoader();
              this.store.dispatch({ type: ReservationActions.setIsSubmiting.type, isSubmiting: false });
              return of(ReservationActions.submitReservationFailure({ error }));
            }))

      })
    );
  });

  // submitReservation$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(ReservationActions.submitReservationSuccess, ReservationActions.submitReservationFailure),
  //     filter(() => this.router.isActive('/tables-plan', exactMatchOptions)),
  //     delay(10),
  //     withLatestFrom(
  //       this.store.select(TablesPlanSelectors.selectConflictingReservations),
  //       this.store.select(TablesPlanSelectors.selectSwapTableNames),
  //     ),
  //     concatMap(async ([action, conflictingReservations, swapTableNames]) => {

  //       this.store.dispatch({
  //         type: TablesPlanActions.setConflictingReservations.type,
  //         reservations: []
  //       });

  //       if (conflictingReservations.length !== 1) {
  //         return EMPTY;
  //       }

  //       const reservation = conflictingReservations[0];

  //       this.store.dispatch({
  //         type: ReservationActions.setReservation.type,
  //         reservation: reservation
  //       });
  //       this.store.dispatch({ 
  //         type: ReservationListActions.setEditingReservationId.type, 
  //         reservationId: reservation.reservationDetails.reservationId 
  //       });

  //       setTimeout(() => {
  //         this.store.dispatch({
  //           type: ReservationActions.setReservationAllocatedTables.type,
  //           allocatedTables: swapTableNames
  //         });
  //       }, 10);

  //       return EMPTY;
  //     })
  //   );
  // }, { dispatch: false });

  saveTablesPlan$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.saveTablesPlan),
      withLatestFrom(this.store.select(TablesPlanSelectors.selectEditingPlan), this.store.select(AppSelectors.selectIsOffline)),
      concatMap(([action, editingPlan, isOffline]) => {

        if (editingPlan == null) {
          return EMPTY;
        }

        if (isOffline === true) {
          return of({}).pipe(
            map(data => {
              return TablesPlanActions.addPendingTablesPlan({ tablesPlan: editingPlan });
            }))
        }

        this.showLoader();

        return this.tablesPlanApiService.update(editingPlan)
          .pipe(
            map(data => {
              this.hideLoader();

              if (data?.ErrorCode != null && data?.ErrorCode.length > 0) {
                return TablesPlanActions.saveTablesPlanFailure({ error: data });
              }

              return TablesPlanActions.saveTablesPlanSuccess();
            }),
            catchError(error => {
              this.hideLoader();
              return of(TablesPlanActions.saveTablesPlanFailure({ error }));
            }))
      })
    );
  });

  saveTablesPlanAsRegular$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.saveTablesPlanAsRegular),
      delay(1000),
      withLatestFrom(this.store.select(TablesPlanSelectors.selectEditingPlan), this.store.select(TablesPlanSelectors.selectWorkingDateCopy)),
      concatMap(([action, editingPlan, workingDateCopy]) => {

        if (editingPlan == null) {
          return EMPTY;
        }

        if (workingDateCopy?.typeOfWorkingDate !== 'special') {
          return of({}).pipe(
            map(data => {
              return TablesPlanActions.deleteOldRegularWorkingDate({ workingDate: null, modifiedBy: action.modifiedBy });
            }))
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return of({}).pipe(
            map(data => {
              return TablesPlanActions.deleteOldRegularWorkingDate({ workingDate: null, modifiedBy: action.modifiedBy });
            }))
        }

        this.showLoader();

        return this.tablesPlanService.getRegularTablesPlan(restaurantId)
          .pipe(
            map(regularWorkingDate => {
              this.hideLoader();

              const workingDate = regularWorkingDate?.typeOfWorkingDate === 'regular' ? regularWorkingDate : null;

              return TablesPlanActions.deleteOldRegularWorkingDate({ workingDate, modifiedBy: action.modifiedBy });
            }),
            catchError(error => {
              this.hideLoader();
              return of(TablesPlanActions.restoreRegularTablesPlanFailure({ error }));
            }))
      })
    );
  });

  restoreRegularTablesPlan$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.restoreRegularTablesPlan, TablesPlanActions.deleteOldRegularWorkingDate),
      delay(1000),
      withLatestFrom(this.store.select(TablesPlanSelectors.selectEditingPlan)),
      concatMap(([action, editingPlan]) => {

        if (editingPlan == null) {
          return EMPTY;
        }

        this.showLoader();

        return this.tablesPlanApiService.update(editingPlan)
          .pipe(
            map(data => {
              this.hideLoader();
              return TablesPlanActions.restoreRegularTablesPlanSuccess();
            }),
            catchError(error => {
              this.hideLoader();
              return of(TablesPlanActions.restoreRegularTablesPlanFailure({ error }));
            }))
      })
    );
  });

  submitPendingTablesPlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.setIsOffline, TablesPlanActions.submitPendingTablesPlan, TablesPlanActions.addPendingTablesPlan),
      withLatestFrom(this.store.select(AppSelectors.selectIsOffline), this.store.select(TablesPlanSelectors.selectPendingEditingPlans)),
      concatMap(([action, isOffline, pendingEditingPlans]) => {

        if (isOffline === true || pendingEditingPlans == null || pendingEditingPlans.length <= 0) {
          return EMPTY;
        }

        const tablesPlan = JSON.parse(JSON.stringify(pendingEditingPlans[0])) as BatchSynchRequestUpdated; // make object mutable

        return this.tablesPlanApiService.update(tablesPlan)
          .pipe(
            map(data => {
              if (data?.ErrorCode != null && data?.ErrorCode.length > 0) {
                return TablesPlanActions.submitPendingTablesPlanFailure(data?.ErrorCode);
              }
              return TablesPlanActions.submitPendingTablesPlanSuccess({ tablesPlan: pendingEditingPlans[0] });
            }),
            catchError(error => of(TablesPlanActions.submitPendingTablesPlanFailure({ error }))))
      }
      )
    );
  });

  submitPendingTablesPlanSuccess$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.submitPendingTablesPlanSuccess),
      withLatestFrom(this.store.select(TablesPlanSelectors.selectPendingEditingPlans)),
      switchMap(([action, pendingEditingPlans]) => {

        this.localStorageService.setPendingTablePlans(pendingEditingPlans);

        this.store.dispatch({ type: ReservationListActions.loadReservations.type });

        return of({}).pipe(
          delay(5000),
          map(data => {
            return TablesPlanActions.submitPendingTablesPlan();
          }))
      }
      )
    );
  });

  saveTablesPlanFailure$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.saveTablesPlanFailure),
      switchMap((action) => {

        const message = this.translateService.instant('tablesPlan.editMode.savePlanErrorMessage');

        this.toastService.show(`${message} ${(action.error as ErrorResponse).ErrorDescription as string}`);

        return of({}).pipe(
          map(data => {
            return TablesPlanActions.emptyAction();
          }))
      }
      )
    );
  });

  savePendingTablePlansToLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.addPendingTablesPlan),
      withLatestFrom(this.store.select(TablesPlanSelectors.selectPendingEditingPlans)),
      switchMap(([action, pendingEditingPlans]) => {

        if (pendingEditingPlans == null || pendingEditingPlans.length <= 0) {
          return EMPTY;
        }

        this.localStorageService.setPendingTablePlans(pendingEditingPlans);

        return of({}).pipe(
          map(data => {
            return TablesPlanActions.addPendingTablesPlanSuccess();
          }))
      }
      )
    );
  });

  loadSettings$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.loadSettings),
      concatMap((action) => {

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        return this.tablesPlanApiService.getRestaurantSettings(restaurantId)
          .pipe(
            map(data => {
              return TablesPlanActions.loadSettingsSuccess(data);
            }),
            catchError(error => {
              return of(TablesPlanActions.loadSettingsFailure({ error }));
            }))
      })
    );
  });

  saveSettings$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(TablesPlanActions.saveSettings),
      concatMap((action) => {

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        return this.tablesPlanApiService.updateRestaurantSettings(restaurantId, action.restaurantSettings)
          .pipe(
            map(data => {
              return TablesPlanActions.saveSettingsSuccess({ restaurantSettings: action.restaurantSettings });
            }),
            catchError(error => {
              return of(TablesPlanActions.saveSettingsFailure({ error }));
            }))
      })
    );
  });

  constructor(
    private actions$: Actions,
    private router: Router,
    private store: Store<State>,
    private tablesPlanApiService: TablesPlanApiService,
    private tablesPlanService: TablesPlanService,
    private reservationApiService: ReservationApiService,
    private reservationService: ReservationService,
    private localStorageService: LocalStorageService,
    private capacitorStorageService: CapacitorStorageService,
    private toastService: ToastService,
    private spinner: NgxSpinnerService,
    private translateService: TranslateService,
  ) { }

  showLoader(noDelay: boolean = false) {

    if (noDelay === true) {
      this.spinner.show();
      return;
    }

    this.loadingSpinnerTimeoutId = setTimeout(() => {
      this.spinner.show();
    }, environment.requestOverlayThreashold);
  }

  hideLoader() {
    if (this.loadingSpinnerTimeoutId != null) {
      clearTimeout(this.loadingSpinnerTimeoutId);
    }

    this.spinner.hide();
  }

  loadingSpinnerTimeoutId: null | ReturnType<typeof setTimeout> = null;
}
